import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Close as CloseIcon,
  Menu as MenuIcon,
  FacebookOutlined as FacebookIcon,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import XIcon from "@mui/icons-material/X";
import {
  Stack,
  Box,
  Grid,
  IconButton,
  Button,
  Typography,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { AuthPath } from "@routers/path/AuthPath";
import { PublicPaths } from "@routers/path/PublicPath";

import { useAppDispatch, useAppSelector } from "@app/hooks";

import { signOut } from "@features/auth/slice";

import { addNewUrlParams } from "@utils/helpers/stringHelper";

import MediumIcon from "@assets/icons/MediumIcon";
import { ReactComponent as UnitedKingdomIcon } from "@assets/images/gatherlance-landing/united-kingdom.svg";

import { useViewport } from "@contexts/ViewportContext";

const aboutLinks = [
  { label: "About us", href: "#" },
  { label: "Press center", href: "#" },
  { label: "How Lanceboard Works", href: "#" },
];

const supportLinks = [
  { label: "Help Center", href: "#" },
  { label: "Term & Policy", href: "#" },
];

export const Header = () => {
  const location = useLocation();
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const {
    palette: { common },
  } = useTheme();
  const { userInfo } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = useMemo(() => {
    if (userInfo) {
      return true;
    }

    return false;
  }, [userInfo]);
  const { isMobile, isTablet, isDesktop } = useViewport();
  const isMobileDesign = isMobile || isTablet;

  const isWePilotApplyPage = location.pathname === PublicPaths.wePilotApply;
  const isDarkMode = isWePilotApplyPage;

  const handleClickSignInAndSignOut = () => {
    if (isLoggedIn) {
      dispatch(signOut());
      navigate(AuthPath.signIn);
    }

    return navigate(
      addNewUrlParams(AuthPath.signIn, {
        redirectTo: PublicPaths.indexPage,
      })
    );
  };

  return (
    <Stack
      px={{ sm: 3, md: 10 }}
      borderBottom={isDarkMode ? common.borderDark : common.border}
      bgcolor={isDarkMode ? common.black2 : ""}
    >
      <Stack
        flexDirection={"row"}
        justifyContent="space-between"
        alignItems={"center"}
        height={"70px"}
        px={isMobile ? 2 : 0}
        // borderBottom={isMobileDesign ? "none" : common.border}
      >
        <Stack
          style={{ cursor: "pointer" }}
          component={"a"}
          href={isDarkMode ? PublicPaths.wePilotPage : PublicPaths.indexPage}
        >
          {isWePilotApplyPage ? (
            <img
              src={"/we_pilot_logo.png"}
              alt="heading-logo"
              height={isMobileDesign ? 32 : 40}
            />
          ) : (
            <img src={"/logo.png"} alt="heading-logo" height={isMobileDesign ? 32 : 40} />
          )}
        </Stack>

        {isMobileDesign ? (
          <IconButton onClick={() => setOpenMobileMenu(!openMobileMenu)} sx={{ px: 0 }}>
            {openMobileMenu ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        ) : (
          <Stack flexDirection={"row"} gap={2} alignItems="center">
            {/* <SignInOutBtn /> */}
            <Button
              sx={{ color: isDarkMode ? "white" : "black" }}
              onClick={handleClickSignInAndSignOut}
            >
              {isLoggedIn ? "Logout" : "Login"}
            </Button>
          </Stack>
        )}
      </Stack>

      {isMobileDesign && openMobileMenu && (
        <Stack rowGap={2} p={3}>
          {/* <SignInOutBtn /> */}
          <Button sx={{ color: "black" }} onClick={handleClickSignInAndSignOut}>
            {isLoggedIn ? "Logout" : "Login"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export const Footer = () => {
  const {
    palette: { common },
  } = useTheme();
  const { isMobile, isTablet, isDesktop } = useViewport();

  const location = useLocation();
  const isDarkMode = location.pathname === PublicPaths.wePilotApply;

  return (
    <>
      <Box
        display={isMobile ? "block" : "flex"}
        justifyContent="space-between"
        py={5}
        px={isDesktop ? 10 : isTablet ? 5 : 2}
        borderTop={isDarkMode ? common.borderDark : common.border}
        bgcolor={isDarkMode ? common.black2 : ""}
      >
        {isDarkMode ? (
          <Box
            component="img"
            src={"/gatherlance_brand_light.png"}
            height={32}
            mb={isMobile ? 3 : 0}
          />
        ) : (
          <Box component="img" src={"/logo.png"} height={32} mb={isMobile ? 3 : 0} />
        )}
        <Grid
          container
          columnSpacing={isDesktop ? 10 : 2}
          rowGap={3}
          justifyContent={isMobile ? "start" : "flex-end"}
        >
          <Grid item xs={11} tablet={6} sm={3}>
            <Stack rowGap={2.5}>
              <Typography fontWeight={700} color={isDarkMode && "white"}>
                ABOUT
              </Typography>
              <Stack rowGap={1.5}>
                {aboutLinks.map((link, index) => {
                  return (
                    <Typography
                      key={`${index}-${link.label}-about-section`}
                      color={isDarkMode ? "white" : common.doverGrey}
                    >
                      {link.label}
                    </Typography>
                  );
                })}
              </Stack>
            </Stack>
          </Grid>
          <Grid item>
            <Stack rowGap={2.5}>
              <Typography fontWeight={700} color={isDarkMode && "white"}>
                SUPPORT
              </Typography>
              <Stack rowGap={1.5}>
                {supportLinks.map((link, index) => {
                  return (
                    <Typography
                      key={`${index}-${link.label}-about-section`}
                      color={isDarkMode ? "white" : common.doverGrey}
                    >
                      {link.label}
                    </Typography>
                  );
                })}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="end"
        py={5}
        px={isDesktop ? 10 : isTablet ? 5 : 2}
        borderTop={isDarkMode ? common.borderDark : common.border}
        bgcolor={isDarkMode ? common.black2 : ""}
      >
        <Stack rowGap={3} width={"100%"}>
          <Stack
            flexDirection={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            gap={3}
          >
            <Button
              startIcon={
                <SvgIcon>
                  <UnitedKingdomIcon />
                </SvgIcon>
              }
              sx={{
                color: isDarkMode ? "white" : "common.black",
                fontWeight: 400,
                width: "fit-content",
                border: common.border,
                borderRadius: 2,
              }}
            >
              English
            </Button>
            <Stack flexDirection={"row"} alignItems={"center"} gap={2}>
              <LinkedInIcon sx={{ color: isDarkMode ? "white" : "black" }} />
              <FacebookIcon sx={{ color: isDarkMode ? "white" : "black" }} />
              <MediumIcon color={isDarkMode ? "white" : "black"} />
              <XIcon sx={{ color: isDarkMode ? "white" : "black" }} />
            </Stack>
          </Stack>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            columnGap={5}
            rowGap={1}
          >
            <Typography color={isDarkMode ? "white" : ""}>
              © {new Date().getFullYear()} Roundtable Corporation
            </Typography>
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              columnGap={2}
              rowGap={1}
            >
              <Typography color={isDarkMode ? "white" : ""}>Terms of Use</Typography>
              <Typography color={isDarkMode ? "white" : ""}>Cookie Policy</Typography>
              <Typography color={isDarkMode ? "white" : ""}>Privacy Notice</Typography>
            </Box>
          </Box>
        </Stack>
        {/* {!isMobile && <IconSystems />} */}
      </Box>
    </>
  );
};
